import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export const useCandidates = (filters = {}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCandidates();
  }, [JSON.stringify(filters)]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);

      // Supabase/PostgREST caps an unranged select at 1000 rows by default -
      // with 2500+ candidates that silently truncated the list. Page through
      // with .range() until a page comes back short, instead of raising the
      // cap (which just moves the ceiling, it doesn't remove it).
      const PAGE_SIZE = 1000;
      const buildPage = (from, to) => {
        let query = supabase
          .from('candidates')
          .select(`
            *,
            position:positions_master(id, name, category),
            candidate_skills(
              id,
              level,
              skill:skills_master(id, name, category)
            )
          `)
          .order('created_at', { ascending: false })
          .range(from, to);

        // Apply filters
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        if (filters.clearance_level) {
          query = query.eq('clearance_level', filters.clearance_level);
        }
        if (filters.location_state) {
          query = query.eq('location_state', filters.location_state);
        }
        if (filters.notice_period) {
          query = query.eq('notice_period', filters.notice_period);
        }
        if (filters.search) {
          // Search across all relevant fields
          query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,location_city.ilike.%${filters.search}%,location_state.ilike.%${filters.search}%,clearance_level.ilike.%${filters.search}%,citizenship_status.ilike.%${filters.search}%,notice_period.ilike.%${filters.search}%`);
        }

        return query;
      };

      let data = [];
      let from = 0;
      while (true) {
        const { data: page, error } = await buildPage(from, from + PAGE_SIZE - 1);
        if (error) throw error;
        data = data.concat(page || []);
        if (!page || page.length < PAGE_SIZE) break;
        from += PAGE_SIZE;
      }

      let filteredData = data || [];

      // Client-side filter for skills (since we need to search nested data)
      if (filters.skill) {
        filteredData = filteredData.filter(candidate =>
          candidate.candidate_skills?.some(cs =>
            cs.skill?.name?.toLowerCase().includes(filters.skill.toLowerCase())
          )
        );
      }

      setCandidates(filteredData);
      setError(null);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { candidates, loading, error, refetch: fetchCandidates };
};

export const useCandidate = (candidateId) => {
  const [candidate, setCandidate] = useState(null);
  const [skills, setSkills] = useState([]);
  const [files, setFiles] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (candidateId) {
      fetchCandidateDetails();
    }
  }, [candidateId]);

  const fetchCandidateDetails = async () => {
    try {
      setLoading(true);

      // Fetch candidate basic info with position
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select(`
          *,
          position:positions_master(id, name, category)
        `)
        .eq('id', candidateId)
        .single();

      if (candidateError) throw candidateError;

      // Fetch skills with skill names
      const { data: skillsData, error: skillsError } = await supabase
        .from('candidate_skills')
        .select(`
          *,
          skill:skills_master(name, category)
        `)
        .eq('candidate_id', candidateId);

      if (skillsError) throw skillsError;

      // Fetch files
      const { data: filesData, error: filesError } = await supabase
        .from('candidate_files')
        .select('*')
        .eq('candidate_id', candidateId);

      if (filesError) throw filesError;

      // Fetch applications with RFQ details
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('applications')
        .select(`
          *,
          rfq:rfqs(ref_code, title, status, client:clients(name))
        `)
        .eq('candidate_id', candidateId);

      if (applicationsError) throw applicationsError;

      setCandidate(candidateData);
      setSkills(skillsData || []);
      setFiles(filesData || []);
      setApplications(applicationsData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching candidate details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { candidate, skills, files, applications, loading, error, refetch: fetchCandidateDetails };
};
